import { VOICE_API_URL } from '@/config/voiceApi'
import { auth } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import type {
  VoiceSession,
  VoiceFragment,
  SessionAnalysis,
  RawSession,
  RawFragment,
  RawSessionAnalysis,
  SessionStatus,
} from './types'

// ─── DTO Mappers (snake_case → camelCase) ─────────────────────────────────

function mapSession(raw: RawSession): VoiceSession {
  return {
    id: raw.id,
    name: raw.name,
    userId: raw.user_id,
    createdAt: raw.created_at,
    status: raw.status as SessionStatus,
    language: raw.language,
    description: raw.description,
    tags: raw.tags,
    totalDuration: raw.total_duration,
    applicationId: raw.application_id,
    projectId: raw.project_id,
    meetingId: raw.meeting_id,
    fragmentCount: raw.fragment_count ?? 0,
  }
}

function mapFragment(raw: RawFragment): VoiceFragment {
  return {
    id: raw.id,
    sessionId: raw.session_id,
    text: raw.text,
    startTime: raw.start_time,
    duration: raw.duration,
    createdAt: raw.created_at,
  }
}

function mapAnalysis(raw: RawSessionAnalysis): SessionAnalysis {
  return {
    id: raw.id,
    sessionId: raw.session_id,
    summary: raw.summary,
    keyQuestions: raw.key_questions || [],
    decisions: raw.decisions || [],
    speakers: raw.speakers || [],
    topics: raw.topics || [],
    modelUsed: raw.model_used,
    customPrompt: raw.custom_prompt,
    customResult: raw.custom_result,
    createdAt: raw.created_at,
  }
}

// ─── HTTP Client ──────────────────────────────────────────────────────────

async function getHeaders(): Promise<Record<string, string>> {
  const authStore = useAuthStore()
  const token = await auth.currentUser?.getIdToken() || ''
  return {
    'Authorization': `Bearer ${token}`,
    'X-User-Id': authStore.userId || '',
    'ngrok-skip-browser-warning': '1',
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${VOICE_API_URL}${path}`
  const headers = await getHeaders()
  const res = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {}),
    },
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Voice API error ${res.status}: ${text}`)
  }

  return res.json()
}

// ─── Sessions ─────────────────────────────────────────────────────────────

export async function fetchSessions(params?: {
  applicationId?: string
  projectId?: string
  meetingId?: string
  status?: SessionStatus
}): Promise<VoiceSession[]> {
  const query = new URLSearchParams()
  if (params?.applicationId) query.set('application_id', params.applicationId)
  if (params?.projectId) query.set('project_id', params.projectId)
  if (params?.meetingId) query.set('meeting_id', params.meetingId)
  if (params?.status) query.set('status', params.status)

  const qs = query.toString()
  const path = qs ? `/sessions?${qs}` : '/sessions'
  const raw = await request<RawSession[]>(path)
  return raw.map(mapSession)
}

export async function updateSession(
  sessionId: number,
  data: Partial<{
    name: string
    status: SessionStatus
    language: string
    description: string
    tags: string
    applicationId: string | null
    projectId: string | null
    meetingId: string | null
  }>,
): Promise<void> {
  const body: Record<string, unknown> = {}
  if (data.name !== undefined) body.name = data.name
  if (data.status !== undefined) body.status = data.status
  if (data.language !== undefined) body.language = data.language
  if (data.description !== undefined) body.description = data.description
  if (data.tags !== undefined) body.tags = data.tags
  if (data.applicationId !== undefined) body.application_id = data.applicationId
  if (data.projectId !== undefined) body.project_id = data.projectId
  if (data.meetingId !== undefined) body.meeting_id = data.meetingId

  await request(`/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function deleteSession(sessionId: number): Promise<void> {
  await request(`/sessions/${sessionId}`, { method: 'DELETE' })
}

// ─── Fragments ────────────────────────────────────────────────────────────

export async function fetchFragments(sessionId: number): Promise<VoiceFragment[]> {
  const raw = await request<RawFragment[]>(`/sessions/${sessionId}/fragments`)
  return raw.map(mapFragment)
}

export async function updateFragment(fragmentId: number, text: string): Promise<void> {
  await request(`/fragments/${fragmentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  })
}

export async function deleteFragment(fragmentId: number): Promise<void> {
  await request(`/fragments/${fragmentId}`, { method: 'DELETE' })
}

// ─── Analysis ────────────────────────────────────────────────────────────

export async function fetchAnalysis(sessionId: number): Promise<SessionAnalysis | null> {
  const raw = await request<RawSessionAnalysis | null>(`/sessions/${sessionId}/analysis`)
  return raw ? mapAnalysis(raw) : null
}

export async function analyzeSession(sessionId: number, prompt?: string): Promise<SessionAnalysis> {
  const options: RequestInit = { method: 'POST' }
  if (prompt) {
    options.headers = { 'Content-Type': 'application/json' }
    options.body = JSON.stringify({ prompt })
  }
  const raw = await request<RawSessionAnalysis>(`/sessions/${sessionId}/analyze`, options)
  return mapAnalysis(raw)
}

export async function analyzeCustom(sessionId: number, prompt: string): Promise<{ customPrompt: string; customResult: string }> {
  const res = await request<{ custom_prompt: string; custom_result: string }>(`/sessions/${sessionId}/analyze-custom`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  })
  return { customPrompt: res.custom_prompt, customResult: res.custom_result }
}

export async function deleteAnalysis(sessionId: number): Promise<void> {
  await request(`/sessions/${sessionId}/analysis`, { method: 'DELETE' })
}
